import type { CalendarEvent } from '@highlight/common'

export interface DayCalendarEvent extends Pick<CalendarEvent, 'id' | 'title' | 'start' | 'end' | 'isAllDay'> {
  calendarName?: string
  color?: string
}

export interface CalendarTimeRange {
  start: Date
  end: Date
}

export interface PositionedCalendarEvent {
  event: DayCalendarEvent
  startMinute: number
  endMinute: number
  column: number
  columnCount: number
}

export const MINUTES_PER_DAY = 24 * 60
export const MIN_EVENT_MINUTES = 15

export function getDayBounds(day: Date): CalendarTimeRange {
  const start = new Date(day)
  start.setHours(0, 0, 0, 0)
  const end = new Date(start)
  end.setDate(end.getDate() + 1)
  return { start, end }
}

export function getMinuteOfDay(date: Date): number {
  return date.getHours() * 60 + date.getMinutes() + date.getSeconds() / 60
}

export function getDayRange(day: Date, range: CalendarTimeRange): { startMinute: number; endMinute: number } | null {
  const { start, end } = getDayBounds(day)
  if (!(range.start < end && range.end > start && range.end > range.start)) {
    return null
  }
  const startMinute = range.start <= start ? 0 : getMinuteOfDay(range.start)
  const endMinute = range.end >= end ? MINUTES_PER_DAY : getMinuteOfDay(range.end)
  // A repeated hour at the autumn clock change can put the wall-clock end before its start.
  return { startMinute, endMinute: Math.max(startMinute + 1, endMinute) }
}

export function layoutDayEvents(
  day: Date,
  events: readonly DayCalendarEvent[],
): {
  allDayEvents: DayCalendarEvent[]
  timedEvents: PositionedCalendarEvent[]
} {
  const allDayEvents: DayCalendarEvent[] = []
  const timedEvents: PositionedCalendarEvent[] = []
  for (const event of events) {
    const range = getDayRange(day, event)
    if (!range) {
      continue
    }
    if (event.isAllDay) {
      allDayEvents.push(event)
    } else {
      timedEvents.push({
        event,
        startMinute: range.startMinute,
        endMinute: Math.min(MINUTES_PER_DAY, Math.max(range.endMinute, range.startMinute + MIN_EVENT_MINUTES)),
        column: 0,
        columnCount: 1,
      })
    }
  }
  timedEvents.sort(
    (a, b) => a.startMinute - b.startMinute || b.endMinute - a.endMinute || a.event.id.localeCompare(b.event.id),
  )

  let group: PositionedCalendarEvent[] = []
  let columnEnds: number[] = []
  let groupEnd = 0
  const finishGroup = () => {
    for (const item of group) {
      item.columnCount = columnEnds.length
    }
    group = []
    columnEnds = []
  }

  for (const item of timedEvents) {
    if (item.startMinute >= groupEnd) {
      finishGroup()
    }
    const freeColumn = columnEnds.findIndex((end) => end <= item.startMinute)
    item.column = freeColumn === -1 ? columnEnds.length : freeColumn
    columnEnds[item.column] = item.endMinute
    group.push(item)
    groupEnd = Math.max(...columnEnds)
  }
  finishGroup()
  return { allDayEvents, timedEvents }
}

/** What's coming up after the selected event matters a little more than what has passed. */
const LATER_EVENT_WEIGHT = 1.25

/**
 * The minute to scroll to the top of a timeline `viewMinutes` tall. The selected
 * event always stays in view, or its start when it is taller than the view.
 * Within that, the view shows as much of the day's other events as it can,
 * leaning towards later ones, and on a tie keeps the selected event near the top.
 * With no selected timed event, it opens on the day's first one, or 8am.
 * `marginMinutes` keeps events off the view's edges.
 */
export function chooseScrollMinute(
  timedEvents: readonly PositionedCalendarEvent[],
  selectedEventId: string | undefined,
  viewMinutes: number,
  marginMinutes: number,
): number {
  const clampTop = (minute: number) => Math.min(Math.max(0, MINUTES_PER_DAY - viewMinutes), Math.max(0, minute))
  const selected = timedEvents.find(({ event }) => event.id === selectedEventId)
  if (!selected) {
    return clampTop((timedEvents[0]?.startMinute ?? 8 * 60) - marginMinutes)
  }

  const earliest = clampTop(selected.endMinute + marginMinutes - viewMinutes)
  const latest = clampTop(selected.startMinute - marginMinutes)
  if (earliest >= latest) {
    return latest
  }

  const others = timedEvents
    .filter((item) => item !== selected)
    .map((item) => ({
      start: item.startMinute - marginMinutes,
      end: item.endMinute + marginMinutes,
      weight: item.startMinute >= selected.startMinute ? LATER_EVENT_WEIGHT : 1,
    }))
  // Each event scores the share of it in view, so the total is piecewise linear
  // in the scroll position and peaks where a view edge meets an event edge.
  const scoreAt = (top: number) =>
    others.reduce((score, { start, end, weight }) => {
      const visible = Math.min(end, top + viewMinutes) - Math.max(start, top)
      return visible > 0 ? score + (weight * visible) / (end - start) : score
    }, 0)
  const candidates = [
    latest,
    earliest,
    ...others.flatMap(({ start, end }) => [start, end, start - viewMinutes, end - viewMinutes]),
  ].filter((top) => top >= earliest && top <= latest)

  let best = { top: latest, score: scoreAt(latest) }
  for (const top of candidates) {
    const score = scoreAt(top)
    if (score > best.score + 1e-9 || (score > best.score - 1e-9 && top > best.top)) {
      best = { top, score }
    }
  }
  return best.top
}
